import { useEffect, useState } from 'react';

import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { styled } from '@classify/react-core-components/styles';
import Dialog from '@classify/react-core-components/Dialog';
import Typography from '@classify/react-core-components/Typography';
import Box from '@classify/react-core-components/Box';
import Button, { Props as ButtonProps } from '@classify/react-core-components/Button';
import HighlightedBulletPoint from '@classify/react-marketing-components/HighlightedBulletPoint';

import { useUserHasSeen } from 'Modules/userLocalStorage';
import { ActionsContainer, BulletList } from 'Modules/layout/components';
import { translate } from '@openclassrooms/translations';

interface ExtendedButtonProps extends ButtonProps {
  target?: boolean;
  'data-event'?: string;
  children?: string;
  href?: string;
}

interface Props {
  userId: string | number;
  storageKey: string;
  shouldSeeComponent?: boolean;
  removeCloseButton?: boolean;
  title?: string;
  description: string;
  bullets?: string[];
  firstButtonProps?: ExtendedButtonProps;
  secondButtonProps?: ExtendedButtonProps;
  trackingImpressionUrl?: string;
  trackingClickUrl?: string;
  media?: React.ReactNode;
}

const InnerWrapper = styled('div')(({ theme: { spacing } }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing(2),
}));

const BlueShiftPopUp = ({
  userId,
  storageKey,
  shouldSeeComponent,
  removeCloseButton,
  title,
  description,
  bullets,
  firstButtonProps,
  secondButtonProps,
  trackingImpressionUrl,
  trackingClickUrl,
  media,
}: Props) => {
  const { hasSeen: hasSeenFeature, setSeen } = useUserHasSeen(storageKey, userId, {
    shouldPreventAutoSeen: true,
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (hasSeenFeature === false && shouldSeeComponent !== false) {
      setIsOpen(true);

      if (trackingImpressionUrl) {
        fetch(trackingImpressionUrl, { method: 'GET', mode: 'no-cors', keepalive: false });
      }
    }
  }, [hasSeenFeature, shouldSeeComponent, trackingImpressionUrl]);

  const handleClose = (reason?: string) => {
    if (reason === 'backdropClick') {
      return;
    }

    setSeen();
    setIsOpen(false);
  };

  const handleAnswer = () => {
    handleClose();

    if (trackingClickUrl) {
      fetch(trackingClickUrl, { method: 'GET', mode: 'no-cors', keepalive: false });
    }
  };

  if (!shouldSeeComponent) {
    return null;
  }

  return (
    <Dialog
      disableScrollLock
      isOpen={isOpen}
      onClose={(event, reason) => {
        handleClose(reason);
      }}
      slotProps={{
        root: {
          sx: {
            height: 0,
            overflow: 'visible',
            top: 'auto',
            '& .MuiDialog-container': {
              overflow: 'visible',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              maxWidth: 1520,
              margin: '0 auto',
              width: '100%',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
            },
            '& .MuiDialog-paper': {
              maxWidth: 320,
              maxHeight: 'unset',
              '& > div': {
                paddingX: 2,
                marginTop: 1,
                marginBottom: 2,
              },
            },
          },
        },
        backdrop: {
          sx: {
            display: 'none',
          },
        },
      }}
    >
      <InnerWrapper>
        {!removeCloseButton && (
          <IconButton
            aria-label={translate('global.action.close_button')}
            color="inherit"
            onClick={handleClose}
            sx={{ alignSelf: 'flex-end' }}
          >
            <CloseIcon />
          </IconButton>
        )}
        {media && <Box sx={{ display: 'flex', '& > *': { maxWidth: '100%' } }}>{media}</Box>}
        <div>
          {title && (
            <Typography component="h1" variant="h6">
              {title}
            </Typography>
          )}

          <Typography component={title ? 'p' : 'h1'} variant="subheadingSmall">
            {description}
          </Typography>
        </div>
        {bullets && bullets.length > 0 && (
          <BulletList>
            {bullets?.map((bullet) => (
              <HighlightedBulletPoint key={bullet} color="info">
                {bullet}
              </HighlightedBulletPoint>
            ))}
          </BulletList>
        )}
        {(firstButtonProps || secondButtonProps) && (
          <ActionsContainer sx={{ justifyContent: 'flex-end' }}>
            {firstButtonProps && (
              <Button variant="tertiary" onClick={handleAnswer} {...firstButtonProps}>
                {firstButtonProps.children}
              </Button>
            )}
            {secondButtonProps && (
              <Button variant="tertiary" onClick={handleAnswer} {...secondButtonProps}>
                {secondButtonProps.children}
              </Button>
            )}
          </ActionsContainer>
        )}
      </InnerWrapper>
    </Dialog>
  );
};

export default BlueShiftPopUp;
